/**
 * The **object/deployable** spawn adapter — a plain RAW-default stat block, no compendium
 * lookup at all — plus the multi-cell group builder that rides it.
 */

import { MAP_ASSET_IDS, mapAsset, squareCell, type EncounterBatchCommand, type Cell, type Combatant, type CombatantBase, type CombatantKind, type EncounterCommand, type ObjectProfile, type Side } from "@lancer-vtt/domain";
import { ok, err, type Result } from "@lancer-vtt/shared";
import { coreActionIdsFor } from "../coreActionCatalog";
import type { SpawnResult } from "./types";

/**
 * Args for {@link spawnObject}. Unlike {@link spawnMech}/{@link spawnNpc}, there is
 * no source to freeze *from* — an object's stats are RAW's own flat defaults (5
 * EVASION, 10 HP per SIZE, 0-4 Armor), so the caller names the shape directly
 * rather than a compendium/roster reference.
 */
export type SpawnObjectArgs = {
  combatantId: string;
  name: string;
  /** SIZE — a multi-section wall is authored as N separate SIZE-1 combatants, not one large SIZE here. */
  size: number;
  /** `"object"` (default) or `"deployable"` — behaviorally identical; only the kind tag differs. */
  kind?: Extract<CombatantKind, "object" | "deployable">;
  /** Tough/fortified material (Core p.87: 1-2 Armor tough, 3-4 fortified). Defaults to 0 (no armor). */
  armor?: number;
  /**
   * EVASION override. Defaults to `5` — RAW's own flat object default (Core p.87).
   */
  evasion?: number;
  /** HP override. Defaults to RAW's 10 per SIZE; a catalogue asset may print its own (the foundry reactor). */
  maxHp?: number;
  /**
   * E-DEFENSE override. Defaults to `0` — RAW's "objects have no E-DEFENSE concept" default. A
   * real per-call value exists for the rare printed object (ms_turret_drones' 10 E-Defense)
   * whose stat block genuinely gives one.
   */
  edef?: number;
  /** The spatial contribution profile (blocksMovement/blocksFlight/blocksSight/cover) — omit for a purely cosmetic/inert prop. `blocksFlight` is dropped unless `blocksMovement` is `true`. */
  objectProfile?: ObjectProfile;
  /**
   * **The DEPLOYER's side**, frozen onto {@link CombatantBase.allegiance} — the side every
   * {@link "@lancer-vtt/domain" sideOf} consumer reads this object back as. Defaults to `"npc"`.
   *
   * **Pass it whenever a deploying combatant exists; omit it for scenery.** A *deployed* object
   * (Jericho cover, a turret drone, a mine) belongs to whoever put it there, so its caller reads
   * `sideOf(deployer)` and hands it in. **Authored terrain has no deployer** — a wall, a boulder,
   * a wreck — and keeps the `"npc"` default.
   *
   * **Why the default is `"npc"` rather than a third neutral value:** {@link
   * "@lancer-vtt/domain" Side} is deliberately the two-valued `"pc" | "npc"`, and widening it
   * for scenery would ripple through the wire, the activation lane and every consumer for no
   * gameplay gain — scenery is `isActor`/`isCharacter`-filtered out of every side-reading path
   * that matters (activation, victory, sitreps, AoE selection). The one lane where a *deployed*
   * object's side is genuinely load-bearing is the hostility test, which is precisely what this
   * parameter fixes.
   */
  allegiance?: Side;
};

/**
 * The one coupling an object profile's flags carry: `blocksFlight` survives only alongside
 * `blocksMovement: true`, the same rule `terrain.ts`'s sparsifiers apply to a cell or edge
 * (`flight-relevant-terrain` D1 — a flight-only barrier has no 2D physical reading). Every object
 * construction path (the OBJECTS tool, `add --object`, `deploy_object`, wreck conversion) runs
 * through {@link spawnObject}, so this is where the rule lives.
 */
function coupledProfile(profile: ObjectProfile): ObjectProfile {
  if (profile.blocksFlight === undefined || profile.blocksMovement === true) return profile;
  const { blocksFlight: _dropped, ...rest } = profile;
  return rest;
}

/**
 * Spawn a destructible object/deployable combatant — RAW's own flat object
 * defaults (Core p.87): **5 EVASION**, **10 HP per SIZE**, an authored **0-4
 * Armor**, no heat/structure/stress track (objects aren't characters), 0 Speed
 * (objects don't move themselves). No compendium lookup, no `compute` — a pure,
 * I/O-free construction, unlike {@link spawnMech}/{@link spawnNpc}.
 *
 * **`evasion`/`edef` are per-call overrides** of the flat 5/0 defaults above;
 * ms_turret_drones' printed 10 Evasion / 10 E-Defense (vs. jericho's RAW-matching 5/0) is
 * the entity the override exists for.
 *
 * The caller mints the combatant `id` (same id discipline as the other adapters)
 * and hands the built `Combatant` to the ordinary `add_combatant` command — this
 * is the `spawnObject(profile)` seam wreck conversion and deployable cover both build on.
 *
 * Returns a rejection for a non-positive SIZE or an out-of-range Armor — the only
 * two structurally-invalid inputs a caller could hand it (there's no compendium
 * lookup to fail).
 */
export function spawnObject(args: SpawnObjectArgs): SpawnResult {
  const { combatantId, name, size, kind = "object", armor = 0, evasion = 5, edef = 0, objectProfile, allegiance = "npc", maxHp = 10 * size } = args;
  if (size <= 0) return err(`spawnObject: size must be positive (got ${size})`);
  if (!(maxHp > 0)) return err(`spawnObject: maxHp must be positive (got ${maxHp})`);
  if (armor < 0 || armor > 4) return err(`spawnObject: armor must be 0-4 (got ${armor})`);

  const base: CombatantBase = {
    maxHp,
    // No heat/structure/stress track — objects aren't characters;
    // `resolveDamageCascade`'s no-structure-track branch (`hasStructureTrack`) destroys an
    // object/deployable outright at 0 HP rather than cascading through a structure table.
    // `maxHeat`/`maxStructure`/`maxStress` simply omitted: presence-based, no `null` sentinel.
    armor,
    // RAW's flat object default is 5 (Core p.87), overridable per call (ms_turret_drones
    // prints 10).
    evasion,
    // No E-DEFENSE concept for MOST objects (RAW: objects aren't characters, no saves/tech
    // targeting) — the `0` default is unread in practice, since a tech attack kind-rejects an
    // object/deployable target before this matters. Overridable per call for the rare printed
    // exception (ms_turret_drones' 10 E-Defense).
    edef,
    speed: 0,
    size,
    // No save bonus and no `saveTarget`: RAW objects aren't characters, so they never roll a
    // save and never force one. `saveBonusFor` resolves an absent axis to the `+0` floor
    // (`:1090`), which is what the old `save: 0` line meant.
    // An object/deployable at 0 HP is destroyed outright — same for both kind values. A wreck
    // (`wreckConversion.ts`) is itself spawned through this adapter, so it inherits the same
    // outright-destroy treatment for free (shooting a wreck destroys it, no conversion).
    destructionOutcome: "destroyOutright",
    // The DEPLOYER's side, `"npc"` when the caller names none. **Don't hardcode `"npc"` here**:
    // a PC-deployed cover section would then read hostile to the player who deployed it, and be
    // offered Overwatch against their own units. Scenery (a wall, a boulder, a wreck) has no
    // deployer and legitimately lands here as `"npc"`. See {@link SpawnObjectArgs.allegiance}.
    allegiance,
    ...(objectProfile ? { objectProfile: coupledProfile(objectProfile) } : {}),
  };
  // An object/deployable's frozen core set is `mech:*`. `spawnMine` delegates to this adapter,
  // so a mine inherits the same core set for free (mirroring how it inherits
  // `destructionOutcome`/`allegiance`).
  base.coreActionIds = coreActionIdsFor("mech");

  return ok({
    id: combatantId,
    kind,
    name,
    base,
    // No `heat` — mirrors `base.maxHeat`'s omission.
    current: { hp: base.maxHp },
    statuses: [],
    pos: null, // unplaced until a `move_token`/`place_token` deploys it onto the map.
    // Objects are GM-authored, GM-owned — no player controls one (mirrors `spawnNpc`).
    controllerSlotId: null,
  });
}

/**
 * Args for {@link spawnObjectGroup}. A multi-section object is authored as N
 * independently-destroyable SIZE-1 sections (Core p.87) — this mints all N at
 * once, sharing one {@link Combatant.objectGroupId},
 * so the authoring surface can place/delete them as one thing while combat still
 * destroys each section independently.
 */
export type SpawnObjectGroupArgs = {
  /**
   * The shared group identity every section carries (`objectGroupId`) — also the
   * id-minting prefix: section *i* becomes `${groupId}:${i}`. Caller-minted, like
   * every other combatant id (the reducer stays deterministic/pure).
   */
  groupId: string;
  /** The wall/boulder's display name — each section is suffixed `(i/N)` when N > 1. */
  name: string;
  /** How many SIZE-1 sections to mint (a SIZE-3 wall ⇒ 3). Must be positive. */
  sectionCount: number;
  /** `"object"` (default) or `"deployable"` — same meaning as {@link spawnObject}. */
  kind?: Extract<CombatantKind, "object" | "deployable">;
  /** Tough/fortified material, applied uniformly to every section. Defaults to 0. */
  armor?: number;
  /**
   * **Per-section SIZE override.** Defaults to `1` — the baseline multi-section walls/boulders
   * rely on; `maxHp: 10 * size` in {@link spawnObject} stays correct by construction for any
   * value. ms_turret_drones' Size 0.5 single-section deploy is the entity this exists for.
   */
  size?: number;
  /** Per-section EVASION override, applied uniformly. Defaults to `5` — see {@link spawnObject}. */
  evasion?: number;
  /** Per-section E-DEFENSE override, applied uniformly. Defaults to `0` — see {@link spawnObject}. */
  edef?: number;
  /** The spatial contribution profile, applied uniformly to every section. */
  objectProfile?: ObjectProfile;
  /** The DEPLOYER's side, applied uniformly to every section — see {@link SpawnObjectArgs.allegiance}. Defaults to `"npc"`. */
  allegiance?: Side;
};

/**
 * Spawn a **group of same-SIZE object/deployable sections** (SIZE 1 by default, per-call
 * overridable) sharing one `objectGroupId`. Each section is otherwise an ordinary {@link spawnObject}
 * combatant — same RAW defaults (or overrides), same 0-HP kind-gated destroy, same `isActor`
 * exclusion — independently destroyable at its own HP. Like {@link spawnObject}, every
 * section spawns **unplaced** (`pos: null`); the authoring surface (a batch of
 * `add_combatant`s, followed by per-section `place_token`/`place`) is the one that chooses
 * adjacent cells and validates non-overlap — this adapter only mints the shape, it does not
 * itself decide geometry (no `World`/adjacency dependency, staying pure/I/O-free like every
 * other spawn adapter).
 *
 * Returns a rejection for a non-positive `sectionCount`, or the first section's
 * `spawnObject` rejection (a structurally-invalid armor) — there's nothing else
 * that can fail.
 */
export function spawnObjectGroup(args: SpawnObjectGroupArgs): Result<Combatant[], string> {
  const { groupId, name, sectionCount, kind = "object", armor = 0, size = 1, evasion, edef, objectProfile, allegiance } = args;
  if (sectionCount <= 0) {
    return err(`spawnObjectGroup: sectionCount must be positive (got ${sectionCount})`);
  }
  const sections: Combatant[] = [];
  for (let i = 0; i < sectionCount; i++) {
    const spawned = spawnObject({
      combatantId: `${groupId}:${i}`,
      name: sectionCount > 1 ? `${name} (${i + 1}/${sectionCount})` : name,
      size,
      kind,
      armor,
      ...(evasion !== undefined ? { evasion } : {}),
      ...(edef !== undefined ? { edef } : {}),
      ...(objectProfile ? { objectProfile } : {}),
      ...(allegiance !== undefined ? { allegiance } : {}),
    });
    if (!spawned.ok) return spawned;
    sections.push({ ...spawned.value, objectGroupId: groupId });
  }
  return ok(sections);
}

/** Which way a sectioned asset's line runs from its anchor: `h` → increasing `x`, `v` → increasing `y`. */
export type AssetAxis = "h" | "v";

/** Args for {@link spawnAsset}. */
export type SpawnAssetArgs = {
  /** The issuing actor, stamped on every command in the batch. */
  actorId: string;
  /** A `MAP_ASSETS` id. An id the catalogue doesn't know is refused. */
  assetId: string;
  /** The anchor cell: the asset's footprint anchor, or a sectioned asset's first section. */
  at: Cell;
  /**
   * The combatant id, or for a sectioned asset the `objectGroupId` its section ids derive from
   * (`${id}:${i}`). Caller-minted, like every combatant id.
   */
  id: string;
  /** Section count for a sectioned asset (the fallen trunk). Defaults to the entry's minimum; refused outside its range, or on an asset that has no sections. */
  sections?: number;
  /** A sectioned asset's line direction. Defaults to `h`. */
  axis?: AssetAxis;
};

/** What {@link spawnAsset} builds: the atomic batch to dispatch, and the combatants it adds. */
export type SpawnAssetResult = {
  command: EncounterBatchCommand;
  combatants: Combatant[];
};

/**
 * Spawn a **map asset** from the `MAP_ASSETS` catalogue (`decisions/0193`) as the ONE atomic
 * `batch` the CLI and the BUILD Objects tool both dispatch, so the two can't disagree: an
 * `add_combatant` (unplaced) + `place_token` pair per combatant, `place_token` threading the
 * reducer's structural placement walls (grid match, footprint fit, overlap). A blocked or
 * off-map cell rejects the whole batch.
 *
 * The entry's mechanics (Size, Evasion, Armor, `objectProfile` with `rooted`/`remains`) are
 * snapshotted into each combatant's `base` through {@link spawnObject}/{@link spawnObjectGroup};
 * every combatant carries `assetId`. A sectioned entry (the fallen trunk) spawns a group of
 * Size-1 sections laid out along `axis` from `at` (square grids only).
 *
 * Refuses an unknown `assetId`, a `sections` value outside the entry's range or on an entry
 * without sections, and a sectioned spawn on a hex grid.
 */
export function spawnAsset(args: SpawnAssetArgs): Result<SpawnAssetResult, string> {
  const { actorId, assetId, at, id, axis = "h" } = args;
  const asset = mapAsset(assetId);
  if (asset === undefined) return err(`spawnAsset: unknown asset "${assetId}" (known: ${MAP_ASSET_IDS.join(", ")})`);
  const shape = { kind: "object" as const, armor: asset.armor, evasion: asset.evasion, objectProfile: asset.objectProfile };

  let spawned: Result<Combatant[], string>;
  let cells: Cell[];
  if (asset.sections === undefined) {
    if (args.sections !== undefined) return err(`spawnAsset: ${assetId} has no sections (got sections ${args.sections})`);
    const one = spawnObject({ combatantId: id, name: asset.label, size: asset.size, ...(asset.hp !== undefined ? { maxHp: asset.hp } : {}), ...shape });
    spawned = one.ok ? ok([one.value]) : one;
    cells = [at];
  } else {
    const { min, max } = asset.sections;
    const count = args.sections ?? min;
    if (!Number.isInteger(count) || count < min || count > max) {
      return err(`spawnAsset: ${assetId} takes ${min}-${max} sections (got ${args.sections})`);
    }
    if (at.grid !== "square") return err(`spawnAsset: ${assetId} lays its sections out on a square grid only`);
    spawned = spawnObjectGroup({ groupId: id, name: asset.label, sectionCount: count, size: asset.size, ...shape });
    cells = Array.from({ length: count }, (_, i) => (axis === "h" ? squareCell(at.x + i, at.y) : squareCell(at.x, at.y + i)));
  }
  if (!spawned.ok) return spawned;

  const combatants = spawned.value.map((c): Combatant => ({ ...c, assetId }));
  const commands: EncounterCommand[] = combatants.flatMap((combatant, i): EncounterCommand[] => [
    { type: "add_combatant", actorId, combatant },
    { type: "place_token", actorId, combatantId: combatant.id, to: cells[i]! },
  ]);
  return ok({ command: { type: "batch", commands }, combatants });
}
